import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { getUserProfileByUid, resolveAccountType } from '../../services/userProfileService';
import { signOutUser, signInWithEmailPassword, signUpWithEmailPassword } from '../../services/authService';
import { sendWebReadyOnce, isInRnWebView } from '../../bridge/webviewBridge';
import { isDesktopWeb } from '../../utils/device';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore';

/**
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ SplashPage — 웹/앱 공통 진입점(루트 `/`). 환경 × 로그인상태로 첫 화면 분기.   │
 * │                                                                           │
 * │  환경 구분: isInRnWebView()=앱(RN 웹뷰) / isDesktopWeb()=PC / 그외=모바일웹  │
 * │                                                                           │
 * │  상태                │ 🖥️ PC          │ 📱 모바일웹      │ 📲 앱(웹뷰)       │
 * │ ─────────────────── │ ─────────────  │ ─────────────  │ ─────────────    │
 * │  비로그인            │ /intro         │ /intro         │ /app/login       │
 * │  가입유형 미선택      │ /app/account-type  (3환경 동일)                     │
 * │  학원(academy)       │ /console       │ /app/home      │ /app/home        │
 * │  학부모 인증완료      │ /app/home      │ /app/home      │ /app/home        │
 * │  학부모 인증안됨      │ /link-phone    │ /link-phone    │ /link-phone      │
 * │                                                                           │
 * │  · 가입완료 = 학부모는 전화인증(phoneE164)까지 / 학원은 가입유형 선택으로     │
 * │  · /console(원장 콘솔)은 랜딩의 "원장님 PC 콘솔" 버튼으로도 진입             │
 * └─────────────────────────────────────────────────────────────────────────┘
 */
const MIN_SPLASH_MS = 2800;

const SplashPage = () => {
  const navigate = useNavigate();
  const { currentUser, loading, refreshUser, userData } = useAuth();
  const resolving = useRef(false);
  const splashStart = useRef(Date.now());
  const [ready, setReady] = useState(false);
  const role = userData?.role;

  const roleResolved = !!role;
  const bgColor = !roleResolved ? '#FFFFFF'
    : role === 'student' ? '#22C55E'
    : role === 'parent' ? '#AB47BC'
    : role === 'admin' ? '#1E293B'
    : '#1C2E54';

  useEffect(() => {
    sendWebReadyOnce();
    // 소셜 로그인 결과는 AuthContext에서 처리
    const timer = setTimeout(() => setReady(true), MIN_SPLASH_MS);
    return () => clearTimeout(timer);
  }, []);

  const pendingNav = useRef(null);

  // 분기 결과가 나오면 바로 이동. (예전 ready 게이팅은 navigate 를 영구 보류시키는
  //  버그가 있어 제거 — 스플래시 최소노출보다 "안 멈추는 것"이 우선)
  const safeNavigate = (path, opts) => {
    navigate(path, opts);
  };

  useEffect(() => {
    if (ready && pendingNav.current) {
      const { path, opts } = pendingNav.current;
      pendingNav.current = null;
      navigate(path, opts);
    }
  }, [ready, navigate]);

  // uid 하나로 프로필 조회 → 가입유형별 첫 화면 분기
  const resolveAndNavigate = async (uid) => {
    const profile = await getUserProfileByUid(uid);
    const accountType = resolveAccountType(profile);
    if (!accountType) {
      // 로그인만 하고 가입유형 미선택 상태로 앱 종료 = 미완료 가입
      // → 로그아웃하고 로그인 화면부터 다시 (카카오/구글 선택)
      await signOutUser();
      safeNavigate('/app/login', { replace: true });
    } else if (accountType === 'academy') {
      // 학원 계정: PC(넓은 화면)는 콘솔로, 폰은 학부모앱 홈(동네 학원 디렉토리)으로.
      //   자기 학원으로 바로 꽂지 않음 — 운영은 검색/마이에서 찾아 들어가서 함
      safeNavigate(isDesktopWeb() ? '/console' : '/app/home', { replace: true });
    } else if (!profile?.phoneE164) {
      // 학부모인데 전화번호 미인증 → 가입 미완. 인증 화면으로 (카카오만 하고 빠져나간 경우)
      safeNavigate('/link-phone', { replace: true });
    } else {
      // 가입유형에 맞는 첫 화면으로 직행 (홈 깜빡임 방지)
      safeNavigate('/app/home', { replace: true });
    }
  };

  useEffect(() => {
    if (loading || resolving.current) return;

    resolving.current = true;

    (async () => {
      try {
        if (!currentUser) {
          // 자동로그인 시도 — 성공하면 반환된 user로 즉시 분기.
          // currentUser 변경을 기다려 재실행에 의존하면, await 도중 onAuthStateChanged가
          // 먼저 발화 → resolving 가드에 막혀 early-return → 이후 재트리거가 없어
          // 스플래시에 영영 멈추는 레이스가 발생함. 그래서 반환값으로 직접 분기한다.
          let signedInUser = null;
          try {
            const saved = JSON.parse(localStorage.getItem('classcare.autoLogin'));
            if (saved?.loginId && saved?.password) {
              const q2 = query(collection(db, 'users'), where('loginId', '==', saved.loginId));
              const snap = await getDocs(q2);
              if (!snap.empty) {
                const ud = snap.docs[0].data();
                if (ud.password === saved.password && (!ud.status || ud.status === 'approved')) {
                  const fakeEmail = `${saved.loginId}@classmanage.local`;
                  try {
                    signedInUser = await signInWithEmailPassword({ email: fakeEmail, password: saved.password, skipEnsure: true });
                  } catch {
                    signedInUser = await signUpWithEmailPassword({ email: fakeEmail, password: saved.password });
                  }
                }
              }
              if (!signedInUser) localStorage.removeItem('classcare.autoLogin');
            }
          } catch {
            localStorage.removeItem('classcare.autoLogin');
          }

          if (signedInUser) {
            await resolveAndNavigate(signedInUser.uid);
          } else if (isInRnWebView()) {
            // 앱(웹뷰): 기존 로그인 흐름
            safeNavigate('/app/login', { replace: true });
          } else {
            // 일반 브라우저(모바일·PC) 방문자: 스플래시 없이 바로 홍보 랜딩으로
            navigate('/intro', { replace: true });
          }
          return;
        }

        await refreshUser();
        await resolveAndNavigate(currentUser.uid);
      } catch (err) {
        console.error('SplashPage 분기 실패:', err);
        safeNavigate('/app/login', { replace: true });
      } finally {
        resolving.current = false;
      }
    })();
  }, [loading, currentUser, navigate, refreshUser]);

  return (
    <Container $bg={bgColor} $resolved={roleResolved}>
      <TitleWrap>
        <Title $resolved={roleResolved}>1등급</Title>
        <Sparkle $resolved={roleResolved}>✦</Sparkle>
      </TitleWrap>
      <SubText $resolved={roleResolved}>학원 운영도, 학부모 소통도<br />일등급 하나로.</SubText>
      <Spinner $resolved={roleResolved} />
    </Container>
  );
};

export default SplashPage;

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${({ $bg }) => $bg};
  gap: 12px;
  transition: background 0.5s ease;
`;

const TitleWrap = styled.div`
  position: relative;
  display: inline-block;
`;

/* 0s ~ 0.8s: 타이틀 페이드 + 살짝 위로 */
const Title = styled.p`
  font-family: 'Apple SD Gothic Neo', -apple-system, sans-serif;
  font-size: 44px;
  font-weight: 800;
  color: ${({ $resolved }) => $resolved ? 'white' : '#1C2E54'};
  letter-spacing: 1px;
  opacity: 0;
  transform: translateY(8px);
  animation: titleIn 0.8s ease forwards;
  transition: color 0.4s;
  @keyframes titleIn {
    to { opacity: 1; transform: translateY(0); }
  }
`;

/* 1.0s ~ 2.5s: 글씨 완전히 나온 뒤 흰색 별 반짝임 */
const Sparkle = styled.span`
  position: absolute;
  top: -4px;
  right: -16px;
  font-size: 18px;
  color: ${({ $resolved }) => $resolved ? 'white' : '#1C2E54'};
  opacity: 0;
  filter: drop-shadow(0 0 6px ${({ $resolved }) => $resolved ? 'rgba(255,255,255,0.8)' : 'rgba(74,124,254,0.5)'});
  animation: sparkle 2s ease-in-out 1.0s forwards;
  transition: color 0.4s, filter 0.4s;
  @keyframes sparkle {
    0% { opacity: 0; transform: scale(0.2) rotate(-30deg); }
    15% { opacity: 1; transform: scale(1.4) rotate(10deg); }
    35% { opacity: 0.9; transform: scale(1) rotate(5deg); }
    55% { opacity: 1; transform: scale(1.2) rotate(8deg); }
    75% { opacity: 0.7; transform: scale(0.9) rotate(0deg); }
    100% { opacity: 0; transform: scale(0.3) rotate(-10deg); }
  }
`;

/* 0.5s ~ 1.1s: 서브텍스트 */
const SubText = styled.p`
  font-size: 14px;
  color: ${({ $resolved }) => $resolved ? 'rgba(255,255,255,0.75)' : '#9CA3AF'};
  letter-spacing: 1px;
  line-height: 1.6;
  text-align: center;
  opacity: 0;
  animation: fadeIn 0.6s ease 0.5s forwards;
  transition: color 0.4s;
  @keyframes fadeIn {
    to { opacity: 1; }
  }
`;

/* 1.2s: 스피너 */
const Spinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid ${({ $resolved }) => $resolved ? 'rgba(255,255,255,0.3)' : '#E5E7EB'};
  border-top-color: ${({ $resolved }) => $resolved ? 'white' : '#1C2E54'};
  border-radius: 50%;
  margin-top: 28px;
  opacity: 0;
  animation: fadeIn 0.4s ease 1.2s forwards, spin 0.8s linear 1.2s infinite;
  transition: border-color 0.4s;
  @keyframes spin { to { transform: rotate(360deg); } }
`;
